import Konva from "konva";
import type Combatant from "@/encounter/model/Combatant.ts";
import {
    getSelectionPivot,
    rotateCombatantsAround,
    setCombatantRotation,
    translateCombatants,
} from "@/tabletop/tokens/TokenTransform.ts";
import type Position from "@/util/Position.ts";
import {useCombatantTransforms} from "@/encounter/combatants/CombatantTransformHooks.ts";
import {getAngleDegrees, getRotationDelta} from "@/util/Rotation.ts";
import {useTableTopInteraction} from "@/tabletop/TableTopInteractionHooks.ts";
import {useTableTopSelection} from "@/tabletop/selection/TableTopSelectionHooks.ts";
import {useTableTopHistory} from "@/tabletop/history/TableTopHistoryHooks.ts";

export function useRoundTokenInteraction(combatant: Combatant) {

    const {selectedCombatants, isCombatantSelected, selectCombatants} = useTableTopSelection();
    const {previewCombatantTransforms, commitCombatantTransforms} = useCombatantTransforms();
    const {beginInteraction, endInteraction} = useTableTopHistory();
    const {
        toWorldPosition,
        getWorldPointer,
        trackWorldPointer,
        startWorldTranslation,
    } = useTableTopInteraction();

    const isSelected = isCombatantSelected(combatant);

    const startMovingCombatants = (
        event: Konva.KonvaEventObject<MouseEvent>,
        combatantsAtMoveStart: Combatant[],
    ) => {
        const stage = event.target.getStage();
        if (!stage) return;

        const getTransforms = (delta: Position) => translateCombatants(combatantsAtMoveStart, delta);
        const started = startWorldTranslation(stage, {
            onCancel: () => endInteraction(),
            onChange: delta => previewCombatantTransforms(getTransforms(delta)),
            onEnd: delta => {
                const after = getTransforms(delta);
                // Always apply the final pointer position so no queued preview can win after mouseup.
                commitCombatantTransforms(after);
                endInteraction(after);
            },
        });
        if (started) beginInteraction({
            type: "combatants",
            ids: combatantsAtMoveStart.map(item => item.id),
        });
    };

    /**
     * Selects and starts moving a token with a normal left click. A token that already
     * belongs to the selection moves the complete selection. Modifier clicks only toggle
     * selection membership and do not start a movement.
     */
    const handleLeftMouseDown = (event: Konva.KonvaEventObject<MouseEvent>) => {
        const shouldToggleSelection = event.evt.shiftKey || event.evt.ctrlKey || event.evt.metaKey;
        if (shouldToggleSelection) {
            const nextSelection = isSelected
                ? selectedCombatants.filter(selected => selected.id !== combatant.id)
                : [...selectedCombatants, combatant];
            selectCombatants(nextSelection);
            return;
        }
        // Replace the selection only when clicking a token outside the current selection.
        const combatantsToMove = isSelected ? selectedCombatants : [combatant];
        if (!isSelected) selectCombatants(combatantsToMove);
        startMovingCombatants(event, combatantsToMove);
    };

    /**
     * Starts rotation with the right mouse button. A single token points directly at
     * the cursor, while multiple tokens rotate by the cursor's angle delta around
     * their shared pivot.
     */
    const handleRightMouseDown = (event: Konva.KonvaEventObject<MouseEvent>) => {
        const stage = event.target.getStage();
        if (!stage) return;

        // Right-clicking outside the selection starts a new single-token selection.
        const combatantsToRotate = isSelected ? selectedCombatants : [combatant];
        if (!isSelected) selectCombatants(combatantsToRotate);

        const startPointerInWorld = getWorldPointer(stage);
        if (!startPointerInWorld) return;

        const isSingleTokenRotation = combatantsToRotate.length === 1;
        const pivot = isSingleTokenRotation
            ? toWorldPosition(event.currentTarget.getAbsolutePosition())
            : getSelectionPivot(combatantsToRotate);

        const getRotationAt = (pointerInWorld: Position) => {
            if (isSingleTokenRotation) {
                const absoluteRotation = getAngleDegrees(pivot, pointerInWorld);
                return setCombatantRotation(combatantsToRotate, absoluteRotation);
            }
            const rotationDelta = getRotationDelta(pivot, startPointerInWorld, pointerInWorld);
            return rotateCombatantsAround(combatantsToRotate, pivot, rotationDelta);
        };

        // A simple right-click must already orient a single token; movement is not required.
        if (isSingleTokenRotation) previewCombatantTransforms(getRotationAt(startPointerInWorld));

        const started = trackWorldPointer(stage, {
            onChange: pointer => previewCombatantTransforms(getRotationAt(pointer)),
            onEnd: pointer => {
                const after = getRotationAt(pointer);
                commitCombatantTransforms(after);
                endInteraction(after);
            },
        });
        if (started) beginInteraction({
            type: "combatants",
            ids: combatantsToRotate.map(item => item.id),
        });
    };

    const handleMouseDown = (event: Konva.KonvaEventObject<MouseEvent>) => {
        if (event.evt.button === 0) {
            event.cancelBubble = true;
            handleLeftMouseDown(event);
        }
        if (event.evt.button === 2) {
            event.cancelBubble = true;
            handleRightMouseDown(event);
        }
    };

    return {isSelected, handleMouseDown};
}
